import React, { useEffect, useRef, useState } from "react";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Star, Send, Image as ImageIcon, Clock, Sparkles, MessageSquare, History, ChevronUp } from "lucide-react";
import Paginated from "../../components/Paginated";
import { useAuth } from "../../lib/auth";
import { fmtINR } from "../../lib/constants";

/**
 * Astrology Live Chat.
 * - Every astrologer profile shows: live status, live timings (from → to),
 *   specializations / USP, average reply time, total chats done, and the
 *   average client rating out of 5 with readable written reviews.
 * - "My previous chats" lists every earlier conversation (one per astrologer
 *   chat), paginated — click any to reopen it.
 * - Messages inside the chat are paginated (latest first, load earlier).
 * - After the free 60 words the customer pays through Razorpay to continue.
 */

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// 12 zodiac signs (Hindu jyotisha order) for the consultation form
const ZODIAC_SIGNS = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];
const PAGE_MSGS = 20;

function slotLabel(s) {
  if (s.type === "weekly") return `${DAY_NAMES[s.day] ?? s.day} ${s.from}–${s.to}`;
  return `${s.date} ${s.from}–${s.to}`;
}

/* ---------- Star row (display) ---------- */
function Stars({ value = 0, size = 12 }) {
  return (
    <span className="inline-flex items-center">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= Math.round(value) ? "fill-gold text-gold" : "text-line"} />
      ))}
    </span>
  );
}

/* ---------- One astrologer profile card ---------- */
function AstrologerCard({ a, selected, onSelect }) {
  const [showReviews, setShowReviews] = useState(false);
  const [reviews, setReviews] = useState(null);

  const openReviews = () => {
    setShowReviews((v) => !v);
    if (!reviews) {
      api.get(`/api/astrology/reviews/${a.id}`).then((r) => setReviews(r.data)).catch(() => setReviews({ reviews: [] }));
    }
  };

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-colors ${selected ? "border-maroon" : "border-line"}`} data-testid={`astro-${a.id}`}>
      <button onClick={() => onSelect(a.id)} className="w-full text-left p-4">
        <div className="flex items-center justify-between">
          <div className="font-display text-lg text-maroon">{a.name}</div>
          <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full ${a.is_live ? "bg-green-100 text-green-800" : "bg-line text-ash"}`}>{a.is_live ? "● Live" : "Offline"}</span>
        </div>

        {/* Specializations / USP */}
        {a.specialization && (
          <div className="flex flex-wrap gap-1 mt-2">
            {a.specialization.split(/[,·|]/).map((sp) => sp.trim()).filter(Boolean).map((sp) => (
              <span key={sp} className="text-[10px] bg-gold/15 border border-gold/40 rounded-full px-2 py-0.5">{sp}</span>
            ))}
          </div>
        )}

        {/* Rating out of 5 · reply time · chats done */}
        <div className="text-xs text-ash mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1">
            <Stars value={a.avg_rating || 0} />
            {a.avg_rating != null ? <b className="text-ink">{a.avg_rating}/5</b> : "New"}
            {a.ratings_count > 0 && <span>({a.ratings_count})</span>}
          </span>
          {a.avg_response_minutes != null && <span className="inline-flex items-center gap-1"><Clock size={11}/> replies in ~{a.avg_response_minutes} min</span>}
          <span className="inline-flex items-center gap-1"><MessageSquare size={11}/> {a.chats_done || 0} consultations</span>
        </div>

        {/* Live timings — from which time to which time they are available */}
        {(a.slots || []).length > 0 && (
          <div className="text-[10px] text-ash mt-2">
            <b className="text-ink">Live timings:</b> {(a.slots || []).slice(0, 4).map(slotLabel).join(" · ")}{(a.slots || []).length > 4 ? " …" : ""}
          </div>
        )}
      </button>

      {/* Reviews — readable by every user & customer, paginated */}
      <div className="border-t border-line/60 px-4 py-2">
        <button onClick={openReviews} className="text-[11px] text-maroon underline" data-testid={`astro-reviews-toggle-${a.id}`}>
          {showReviews ? "Hide reviews" : `Read reviews${a.ratings_count ? ` (${a.ratings_count})` : ""}`}
        </button>
        {showReviews && (
          <div className="mt-2">
            {!reviews ? <div className="text-xs text-ash py-2">Loading…</div> : (reviews.reviews || []).length === 0 ? (
              <div className="text-xs text-ash py-2">No reviews yet — be the first to rate after your consultation.</div>
            ) : (
              <Paginated items={reviews.reviews} pageSize={3}>
                {(pageItems) => (
                  <div className="space-y-2">
                    {pageItems.map((r, i) => (
                      <div key={i} className="bg-pearl/60 border border-line/60 rounded-lg p-2 text-xs">
                        <div className="flex items-center justify-between">
                          <b>{r.customer_name || "Customer"}</b>
                          <Stars value={r.rating} size={10}/>
                        </div>
                        {r.review && <div className="text-ash mt-1">“{r.review}”</div>}
                        <div className="text-[9px] text-ash mt-0.5">{r.rated_at ? new Date(r.rated_at).toLocaleDateString("en-GB") : ""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Paginated>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Astrology() {
  const { user } = useAuth();
  const [astrologers, setAstrologers] = useState([]);
  const [liveStatus, setLiveStatus] = useState(null);
  const [chatId, setChatId] = useState(() => localStorage.getItem("astro_chat_id") || "");
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_MSGS); // message pagination
  const [myChats, setMyChats] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: localStorage.getItem("astro_phone") || "", question: "", astrologer_id: "",
    dob: "", birth_hour: "", birth_minute: "", birth_ampm: "AM", birthplace: "", zodiac: "",
  });
  const [msg, setMsg] = useState("");
  const [needPay, setNeedPay] = useState(false);
  const [payWall, setPayWall] = useState(null); // { reason: 'word_limit'|'image', photo_price, premium_price }
  const [imgBusy, setImgBusy] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [myReview, setMyReview] = useState("");
  const [showRateBox, setShowRateBox] = useState(false);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const listRef = useRef(null);

  // Prefill the consult form for a logged-in customer (name / phone / DOB) —
  // all still editable. Birth time & place stay blank for them to fill.
  useEffect(() => {
    if (!user) return;
    api.get("/api/me/overview").then((r) => {
      const c = r.data?.customer || {};
      setForm((f) => ({
        ...f,
        name: f.name || r.data?.profile?.name || user.name || "",
        phone: f.phone || (c.phone || user.phone || "").replace(/\D/g, "").slice(-10),
        dob: f.dob || c.birthday || "",
      }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAstrologers = () => {
    api.get("/api/astrology/astrologers").then((r) => setAstrologers(r.data || [])).catch(() => {});
    api.get("/api/astrology/live-status").then((r) => setLiveStatus(r.data)).catch(() => {});
  };
  const loadMyChats = () => {
    const phone = localStorage.getItem("astro_phone") || "";
    if (!phone && !user) { setMyChats([]); return; }
    api.get(`/api/astrology/my-chats${phone ? `?phone=${phone}` : ""}`).then((r) => setMyChats(r.data || [])).catch(() => {});
  };
  const loadChat = (id) => {
    if (!id) return;
    api.get(`/api/astrology/chat/${id}`).then((r) => {
      setChat(r.data.chat); setMessages(r.data.messages || []);
      if (r.data.chat?.rating) { setMyRating(r.data.chat.rating); setMyReview(r.data.chat.review || ""); }
    }).catch(() => { localStorage.removeItem("astro_chat_id"); setChatId(""); });
  };

  // Refresh every 10 seconds — astrologer list when choosing, messages while chatting
  useEffect(() => {
    loadAstrologers();
    loadMyChats();
    const t = setInterval(() => {
      if (chatId) loadChat(chatId); else loadAstrologers();
    }, 10000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [chatId]);
  useEffect(() => { if (chatId) { loadChat(chatId); setVisibleCount(PAGE_MSGS); } /* eslint-disable-next-line */ }, [chatId]);
  useEffect(() => {
    // Scroll ONLY the message list, never the whole page.
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const start = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.phone)) { toast.error("Enter a valid 10-digit phone number"); return; }
    if (!form.astrologer_id) { toast.error("Choose an astrologer from the list"); return; }
    // Combine the hour/minute/AM-PM into one readable birth_time string
    const birth_time = (form.birth_hour && form.birth_minute)
      ? `${form.birth_hour}:${form.birth_minute} ${form.birth_ampm}`
      : "";
    const payload = {
      name: form.name, phone: form.phone, question: form.question, astrologer_id: form.astrologer_id,
      dob: form.dob, birth_time, birthplace: form.birthplace, zodiac: form.zodiac,
    };
    try {
      const { data } = await api.post("/api/astrology/start", payload);
      localStorage.setItem("astro_chat_id", data.chat_id);
      localStorage.setItem("astro_phone", form.phone); // so previous chats can be found later
      setChatId(data.chat_id);
      loadMyChats();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start the chat"); }
  };

  const openPrevious = (c) => {
    localStorage.setItem("astro_chat_id", c.id);
    setChatId(c.id);
    setMyRating(c.rating || 0);
    setShowRateBox(false);
  };

  const send = async (imageUrl = "") => {
    if (!msg.trim() && !imageUrl) return;
    try {
      const { data } = await api.post("/api/astrology/message", { chat_id: chatId, message: msg || "(image)", sender: "customer", image_url: imageUrl });
      if (data.requires_payment) {
        setPayWall({ reason: data.reason, photo_price: data.photo_price, premium_price: data.premium_price });
        setNeedPay(true);
        return false;
      }
      setMsg("");
      loadChat(chatId);
      return true;
    } catch (err) {
      // Banned-keyword rejection / 5-image cap
      toast.error(err?.response?.data?.detail || "Message not sent");
      return false;
    }
  };

  const attachImage = async (file) => {
    if (!file) return;
    setImgBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "astro-chat");
      const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const base = (api.defaults.baseURL || "").replace(/\/$/, "");
      await send(`${base}${data.url}`);
    } catch { toast.error("Image upload failed"); }
    finally { setImgBusy(false); }
  };

  const pay = async (purpose = "premium") => {
    try {
      const { data } = await api.post(`/api/astrology/${chatId}/pay/create-order`, { purpose });
      const verify = async (orderId, paymentId, sig, method) => {
        await api.post(`/api/astrology/${chatId}/pay/verify`, { razorpay_order_id: orderId, razorpay_payment_id: paymentId, razorpay_signature: sig, method });
        toast.success(purpose === "photo" ? "Photos unlocked — you can attach up to 5 images" : "Premium unlocked — continue your consultation");
        setNeedPay(false); setPayWall(null); loadChat(chatId);
      };
      if (data.mock) {
        await verify(data.order_id, `pay_mock_astro_${Date.now()}`, "mock", "test");
      } else {
        const rzp = new window.Razorpay({
          key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
          name: "Swayambhu Gold — Astrology", description: purpose === "photo" ? "Attach photos" : "Premium consultation unlock",
          prefill: { name: chat?.customer_name, contact: chat?.customer_phone },
          handler: (resp) => verify(resp.razorpay_order_id, resp.razorpay_payment_id, resp.razorpay_signature, resp.method || "razorpay"),
        });
        rzp.open();
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Payment failed"); }
  };

  const submitRating = async () => {
    if (!myRating) { toast.error("Pick a star rating first"); return; }
    try {
      await api.post(`/api/astrology/${chatId}/rate`, { rating: myRating, review: myReview });
      toast.success("Thanks for your rating & review!");
      setShowRateBox(false);
      loadAstrologers();
    } catch { toast.error("Could not save rating"); }
  };

  // Message pagination — show the latest N, reveal earlier ones on demand
  const visibleMessages = messages.slice(Math.max(0, messages.length - visibleCount));
  const hiddenCount = messages.length - visibleMessages.length;

  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-10 max-w-6xl mx-auto">
        <div className="eyebrow">Guidance</div>
        <h1 className="font-display text-4xl lg:text-5xl text-maroon">Live Astrology Consultation</h1>
        <p className="text-sm text-ash mt-2 mb-6">First 60 words free — then unlock a full premium consultation.</p>

        {liveStatus && !liveStatus.any_live && !chatId && (
          <div className="bg-gold/10 border border-gold p-4 text-sm mb-6" data-testid="no-astro-live">
            No astrologer is live right now. You can still leave your question — the next available astrologer will reply as soon as they come online.
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT: astrologer list (hidden while chatting) */}
          {!chatId && (
            <div className="lg:col-span-1 space-y-3" data-testid="astrologer-list">
              <div className="eyebrow text-[10px]">Choose your astrologer</div>
              <Paginated items={astrologers} pageSize={4}>
                {(pageItems) => (
                  <div className="space-y-3">
                    {pageItems.map((a) => (
                      <AstrologerCard key={a.id} a={a} selected={form.astrologer_id === a.id} onSelect={(id) => setForm({ ...form, astrologer_id: id })} />
                    ))}
                  </div>
                )}
              </Paginated>
              {astrologers.length === 0 && <div className="text-xs text-ash">No astrologers onboarded yet.</div>}
            </div>
          )}

          {/* RIGHT: start form + previous chats, or the chat itself */}
          <div className={chatId ? "lg:col-span-3" : "lg:col-span-2"}>
            {!chatId ? (
              <div className="space-y-6">
                <form onSubmit={start} className="bg-white border border-line p-6 space-y-3" data-testid="astro-start-form">
                  <div className="font-display text-2xl text-maroon flex items-center gap-2"><Sparkles size={18} className="text-gold"/> Ask your question</div>
                  <div className="grid grid-cols-2 gap-3">
                    <input required placeholder="Your name" className="border border-line px-3 py-2 bg-pearl" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="astro-name"/>
                    <input required placeholder="Phone (10 digits)" maxLength={10} className="border border-line px-3 py-2 bg-pearl" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10)})} data-testid="astro-phone"/>
                  </div>

                  {/* Birth details for the reading */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Date of birth</label>
                      <input required type="date" className="w-full border border-line px-3 py-2 bg-pearl" value={form.dob} onChange={(e) => setForm({...form, dob: e.target.value})} data-testid="astro-dob"/>
                    </div>
                    <div>
                      <label className="text-[10px] text-ash uppercase tracking-widest block mb-1">Birth time</label>
                      <div className="flex gap-1">
                        <select required className="border border-line px-2 py-2 bg-pearl flex-1" value={form.birth_hour} onChange={(e) => setForm({...form, birth_hour: e.target.value})} data-testid="astro-birth-hour">
                          <option value="">HH</option>
                          {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                        <select required className="border border-line px-2 py-2 bg-pearl flex-1" value={form.birth_minute} onChange={(e) => setForm({...form, birth_minute: e.target.value})} data-testid="astro-birth-minute">
                          <option value="">MM</option>
                          {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select className="border border-line px-2 py-2 bg-pearl" value={form.birth_ampm} onChange={(e) => setForm({...form, birth_ampm: e.target.value})} data-testid="astro-birth-ampm">
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input required placeholder="Birthplace (city / town)" className="border border-line px-3 py-2 bg-pearl" value={form.birthplace} onChange={(e) => setForm({...form, birthplace: e.target.value})} data-testid="astro-birthplace"/>
                    <select required className="border border-line px-3 py-2 bg-pearl" value={form.zodiac} onChange={(e) => setForm({...form, zodiac: e.target.value})} data-testid="astro-zodiac">
                      <option value="">Zodiac sign (Rashi)</option>
                      {ZODIAC_SIGNS.map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                  </div>

                  <textarea required placeholder="Your question (first 60 words free)" rows={4} className="w-full border border-line px-3 py-2 bg-pearl" value={form.question} onChange={(e) => setForm({...form, question: e.target.value})} data-testid="astro-question"/>
                  <div className="text-xs text-ash">Selected astrologer: <b className="text-maroon">{astrologers.find((a) => a.id === form.astrologer_id)?.name || "— pick one from the list"}</b></div>
                  <button className="btn-maroon w-full justify-center" data-testid="astro-start">Start Consultation</button>
                </form>

                {/* MY PREVIOUS CHATS — one per conversation, paginated */}
                {myChats.length > 0 && (
                  <div data-testid="astro-my-chats">
                    <div className="eyebrow text-[10px] mb-2 flex items-center gap-1"><History size={12}/> Your previous consultations ({myChats.length})</div>
                    <Paginated items={myChats} pageSize={4}>
                      {(pageItems) => (
                        <div className="space-y-2">
                          {pageItems.map((c) => (
                            <button key={c.id} onClick={() => openPrevious(c)} className="w-full text-left bg-white border border-line hover:border-maroon rounded-xl p-3 transition-colors" data-testid={`astro-prev-${c.id}`}>
                              <div className="flex items-center justify-between">
                                <b className="text-maroon text-sm">{c.astrologer_name}</b>
                                <span className="text-[10px] text-ash">{new Date(c.last_at || c.created_at).toLocaleDateString("en-GB")}</span>
                              </div>
                              <div className="text-xs text-ash mt-1 truncate">{c.last_message || "No messages yet"}</div>
                              <div className="text-[10px] text-ash mt-1 flex items-center gap-2">
                                <span>{c.messages_count} messages</span>
                                {c.is_paid && <span className="text-gold-dark">Premium</span>}
                                {c.rating ? <span className="inline-flex items-center gap-0.5">You rated <Stars value={c.rating} size={9}/></span> : null}
                                <span className="text-maroon underline ml-auto">Open chat →</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </Paginated>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border border-line flex flex-col" style={{ height: "70vh" }} data-testid="astro-chat">
                <div className="p-3 border-b border-line flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg text-maroon">{chat?.astrologer_name || "Awaiting astrologer"}</div>
                    <div className="text-[10px] text-ash">{chat?.is_paid ? "Premium consultation" : `Free words used: ${chat?.free_words_used ?? 0}/${chat?.free_words_limit ?? 60}`}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Rate & review the astrologer */}
                    <button onClick={() => setShowRateBox((v) => !v)} className="flex items-center gap-1 text-xs border border-line rounded-full px-3 py-1.5 hover:border-gold transition-colors" data-testid="astro-rate-toggle">
                      <Stars value={myRating} size={12}/> {myRating ? "Edit rating" : "Rate"}
                    </button>
                    <button onClick={() => { localStorage.removeItem("astro_chat_id"); setChatId(""); setChat(null); setMessages([]); setMyRating(0); setMyReview(""); setShowRateBox(false); loadMyChats(); }} className="text-xs text-maroon underline" data-testid="astro-new-chat">All chats</button>
                  </div>
                </div>

                {showRateBox && (
                  <div className="p-3 border-b border-gold bg-gold/10 space-y-2" data-testid="astro-rate-box">
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map((n) => (
                        <button key={n} onClick={() => setMyRating(n)} title={`Rate ${n}`} data-testid={`rate-${n}`}>
                          <Star size={20} className={n <= myRating ? "fill-gold text-gold" : "text-line"}/>
                        </button>
                      ))}
                      <span className="text-xs text-ash ml-2">{myRating ? `${myRating}/5` : "Tap to rate"}</span>
                    </div>
                    <textarea rows={2} placeholder="Write a short review (optional) — other users will read it" className="w-full border border-line px-3 py-2 bg-white text-sm" value={myReview} onChange={(e) => setMyReview(e.target.value)} data-testid="astro-review-text"/>
                    <button onClick={submitRating} className="btn-maroon text-xs" data-testid="astro-review-submit">Submit rating & review</button>
                  </div>
                )}

                <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                  {/* Message pagination — load earlier messages */}
                  {hiddenCount > 0 && (
                    <div className="text-center">
                      <button onClick={() => setVisibleCount((c) => c + PAGE_MSGS)} className="inline-flex items-center gap-1 text-xs border border-line rounded-full px-4 py-1.5 hover:border-maroon transition-colors" data-testid="astro-load-earlier">
                        <ChevronUp size={12}/> Load earlier messages ({hiddenCount} more)
                      </button>
                    </div>
                  )}
                  {visibleMessages.map((m) => (
                    <div key={m.id} className={`max-w-[80%] ${m.sender === "customer" ? "ml-auto" : ""}`}>
                      <div className={`p-3 text-sm ${m.sender === "customer" ? "bg-maroon text-pearl" : "bg-pearl border border-line"}`}>
                        {m.image_url && <img src={m.image_url} alt="" className="max-h-40 mb-2 border border-line"/>}
                        {m.message}
                        {/* Astrologer gemstone suggestions */}
                        {(m.attachments || []).length > 0 && (
                          <div className="mt-2 space-y-2">
                            {m.attachments.map((g, gi) => (
                              <div key={gi} className="bg-white text-ink border border-gold rounded-lg p-2 flex gap-2" data-testid={`astro-gem-${gi}`}>
                                {g.image && <img src={g.image} alt={g.name} className="w-14 h-14 object-cover rounded shrink-0"/>}
                                <div className="min-w-0">
                                  <div className="font-medium text-maroon text-[13px]">{g.name}{g.carat ? ` · ${g.carat}ct` : ""}</div>
                                  {g.wear_metal && g.wear_metal !== "None" && <div className="text-[10px] text-ash">Wear in: <b>{g.wear_metal}</b></div>}
                                  {g.caption && <div className="text-[11px] text-ink mt-0.5">{g.caption}</div>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-[9px] text-ash mt-0.5">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{m.sender === "astrologer" && m.response_minutes != null && ` · replied in ${m.response_minutes} min`}</div>
                    </div>
                  ))}
                  <div ref={bottomRef}/>
                </div>
                {needPay ? (
                  <div className="p-4 border-t border-gold bg-gold/10 text-center" data-testid="astro-paywall">
                    {payWall?.reason === "image" ? (
                      <>
                        <div className="text-sm font-medium">Sharing photos is a premium feature.</div>
                        <p className="text-xs text-ash mt-1">Pay {payWall?.photo_price ? fmtINR(payWall.photo_price) : "the photo fee"} to attach photos — up to 5 per consultation.</p>
                        <div className="flex gap-2 justify-center mt-3">
                          <button onClick={() => pay("photo")} className="btn-maroon" data-testid="astro-pay-photo">Pay & attach photos</button>
                          <button onClick={() => { setNeedPay(false); setPayWall(null); }} className="btn-outline text-sm">Not now</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm font-medium">Your free words are used up.</div>
                        <p className="text-xs text-ash mt-1">Unlock the full premium consultation to continue chatting.</p>
                        <button onClick={() => pay("premium")} className="btn-maroon mt-3" data-testid="astro-pay">Pay &amp; Continue (Razorpay)</button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="p-3 border-t border-line flex gap-2">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => attachImage(e.target.files?.[0])}/>
                    <button onClick={() => fileRef.current?.click()} disabled={imgBusy} className="border border-line px-3 text-ash hover:text-maroon" title="Attach image" data-testid="astro-attach"><ImageIcon size={16}/></button>
                    <input placeholder="Type your message…" className="flex-1 border border-line px-3 py-2 bg-pearl" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="astro-input"/>
                    <button onClick={() => send()} className="btn-maroon" data-testid="astro-send"><Send size={14}/></button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}
